import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { I18nProvider } from './lib/i18n';
import { AuthProvider, useAuth } from './lib/useAuth';
import { useAdminAccess } from './lib/platformAdmins';
import { NAV_ITEMS } from './lib/nav';
import AppShell from './components/shell/AppShell';
import TenantGate from './components/TenantGate';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Inbox from './pages/Inbox';
import Subscribers from './pages/Subscribers';
import Broadcasting from './pages/Broadcasting';
import Connect from './pages/Connect';
import WhatsAppProfile from './pages/WhatsAppProfile';
import Templates from './pages/Templates';
import BotManager from './pages/BotManager';
import AutoReplies from './pages/AutoReplies';
import ChatWidget from './pages/ChatWidget';
import ECommerceCatalog from './pages/ECommerceCatalog';
import StoreAutomation from './pages/StoreAutomation';
import HttpApi from './pages/HttpApi';
import Forms from './pages/Forms';
import FormFill from './pages/FormFill';
import Appointments from './pages/Appointments';
import WhatsAppAds from './pages/WhatsAppAds';
import Sequences from './pages/Sequences';
import InputFlows from './pages/InputFlows';
import AiAgent from './pages/AiAgent';
import OnboardingGuide from './pages/OnboardingGuide';
import UserPermission from './pages/UserPermission';
import UserManager from './pages/UserManager';
import Settings from './pages/Settings';
import Integrations from './pages/Integrations';
import Billing from './pages/Billing';
import Support from './pages/Support';
import PlatformAdmin from './pages/PlatformAdmin';
import ComingSoon from './pages/ComingSoon';
import GoogleIntegration from './pages/GoogleIntegration';
import Flows from './pages/Flows';
import FlowBuilder from './pages/FlowBuilder';
import WebhookWorkflows from './pages/WebhookWorkflows';
import InvoiceTemplateEditor from './pages/InvoiceTemplateEditor';
import { PermissionsProvider } from './lib/permissions';
import { AccountsProvider } from './lib/accounts';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import ErrorBoundary from './components/ErrorBoundary';

// Real pages, keyed by route path. Anything not here falls back to a
// "coming in P<n>" stub.
const BUILT_PAGES: Record<string, React.ReactElement> = {
  '/inbox': <Inbox />,
  '/subscribers': <Subscribers />,
  '/broadcasting': <Broadcasting />,
  '/connect': <Connect />,
  '/whatsapp-profile': <WhatsAppProfile />,
  '/bots': <BotManager />,
  '/onboarding': <OnboardingGuide />,
  '/permissions': <UserPermission />,
  '/user-manager': <UserManager />,
  '/settings': <Settings />,
  '/integrations': <Integrations />,
  '/billing': <Billing />,
  '/catalog': <ECommerceCatalog />,
  // WP-12: Store Automation ('/store-automation' is a redirect below, not a page)
  '/store': <StoreAutomation />,
  '/forms': <Forms />,
  '/appointment': <Appointments />,
  '/ads': <WhatsAppAds />,
  // WP-05: Google integration
  '/google-integration': <GoogleIntegration />,
  // WP-13: HTTP API manager
  '/http-api': <HttpApi />,
  // WP-14: Webhook workflows ('/webhooks' is a redirect below, not a page)
  '/webhook-workflows': <WebhookWorkflows />,
  // WP-08: Visual Flows
  '/flows': <Flows />,
};

function Protected({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-surface">
        <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  return user ? children : <Navigate to="/login" replace />;
}

// Platform Admin gate: must be signed in AND either on the root allowlist
// (tagnologys@gmail.com) or hold the `platformAdmin` custom claim that
// padminGrantStaff issues to Tagnologys staff. Non-admins are bounced to the
// tenant dashboard so the console never renders for them. Real enforcement is
// server-side too (requirePlatformAdmin accepts exactly the same two paths).
function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  const access = useAdminAccess(user);
  if (loading || (user && access.allowed === null)) {
    return (
      <div className="min-h-screen grid place-items-center bg-surface">
        <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!access.allowed) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Public self-serve signup — the front door for new customers. */}
              <Route path="/signup" element={<Signup />} />
              {/* Public form fill page — no auth (PRD §4.13). */}
              <Route path="/f/:tenantId/:formId" element={<FormFill />} />
              <Route
                element={
                  <Protected>
                    <TenantGate>
                      <AppShell />
                    </TenantGate>
                  </Protected>
                }
              >
                <Route index element={<Dashboard />} />
                {/* Templates has no sidebar entry — it's reached from the Bot
                    Manager "Message Template" card (PRD §4.4). */}
                <Route path="templates" element={<Templates />} />
                {/* Auto-replies editor — reached from the Bot Manager "Auto Reply" card. */}
                <Route path="bot/auto-replies" element={<AutoReplies />} />
                <Route path="bot/chat-widget" element={<ChatWidget />} />
                <Route path="bot/sequences" element={<Sequences />} />
                <Route path="sequences" element={<Navigate to="/bot/sequences" replace />} />
                <Route path="bot/input-flows" element={<InputFlows />} />
                <Route path="input-flows" element={<Navigate to="/bot/input-flows" replace />} />
                <Route path="bot/ai-agent" element={<AiAgent />} />
                <Route path="ai-agents" element={<Navigate to="/bot/ai-agent" replace />} />
                <Route path="store-automation" element={<Navigate to="/store" replace />} />
                <Route path="store/:connectionId/invoice" element={<InvoiceTemplateEditor />} />
                {/* '/webhook-workflows' itself is a NAV_ITEM and is routed by the map below. */}
                <Route path="webhooks" element={<Navigate to="/webhook-workflows" replace />} />
                {/* Support tickets — reached from the topbar Help menu (PRD §4.17). */}
                <Route path="support" element={<Support />} />
                {/* Every nav destination that isn't built yet gets a "coming in P<n>" stub.
                    NOTE: a page is only reachable if its path is in NAV_ITEMS OR has an
                    explicit <Route> above. Adding to BUILT_PAGES alone does nothing. */}
                {NAV_ITEMS.filter((item) => item.path !== '/').map((item) => (
                  <Route
                    key={item.path}
                    path={item.path.slice(1)}
                    element={
                      BUILT_PAGES[item.path] ?? (
                        <ComingSoon titleKey={item.key} phase={item.phase ?? 0} icon={item.icon} />
                      )
                    }
                  />
                ))}
              </Route>
              {/* WP-08: Visual Flow Builder Fullscreen Canvas */}
              <Route
                path="/flows/new"
                element={
                  <Protected>
                    <TenantGate>
                      <AccountsProvider>
                        <ToastProvider>
                          <ConfirmProvider>
                            <ErrorBoundary>
                              <FlowBuilder />
                            </ErrorBoundary>
                          </ConfirmProvider>
                        </ToastProvider>
                      </AccountsProvider>
                    </TenantGate>
                  </Protected>
                }
              />
              <Route
                path="/flows/:id"
                element={
                  <Protected>
                    <TenantGate>
                      <AccountsProvider>
                        <ToastProvider>
                          <ConfirmProvider>
                            <ErrorBoundary>
                              <FlowBuilder />
                            </ErrorBoundary>
                          </ConfirmProvider>
                        </ToastProvider>
                      </AccountsProvider>
                    </TenantGate>
                  </Protected>
                }
              />
              {/* Platform Admin — staff-only console (PRD §5). Gated to the
                  platform-admin allowlist (tagnologys@gmail.com) on the client via
                  RequireAdmin, and independently enforced server-side by
                  requirePlatformAdmin on every padmin callable + Firestore rules. */}
              <Route
                path="/padmin"
                element={
                  <RequireAdmin>
                    {/* PlatformAdmin had no ToastProvider: every toast inside
                        it silently went to console.log. */}
                    <ToastProvider>
                      <ConfirmProvider>
                        <ErrorBoundary>
                          <PlatformAdmin />
                        </ErrorBoundary>
                      </ConfirmProvider>
                    </ToastProvider>
                  </RequireAdmin>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
